import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FilterPanel } from '@gsa/afp-component-library';
import { useFederalStandards } from '../../fvs-provider';
import useFilterPanelReset from '../../../../hooks/use-filter-panel-reset';
import VehicleTypeFilterItem from './vehicle-classification-filters/vehicle-type-filter';
import VehicleGroupFilterItem from './vehicle-classification-filters/vehicle-group-filter';
import FederalStandardCodeFilterItem from './vehicle-classification-filters/fsc-filter';
import {
  FED_STANDARD_CODE_FILTER,
  VEHICLE_GROUP_FILTER,
  VEHICLE_TYPE_FILTER,
  FEDERAL_STANDARDS_HIERARCHY,
} from '../../../fvs/landing-page/components/vehicle-classification-filters/filter-item-constants';

// eslint-disable-next-line
const FvsFilter = ({ onFilter }) => {
  const {
    vehicleClassificationOptions,
    selectedYear,
    selectedOptions,
    applicableFvsYears,
    isApplicableFvsYearsLoading,
    setFvsContextData,
    removeFvsMessageById,
  } = useFederalStandards();

  const defaultYear = applicableFvsYears?.find(
    (year) => year.isDefault === true,
  );

  const { FilterPanel: ComponentFilterPanel } = FilterPanel;

  const filterStructure = useMemo(() => [
    {
      key: 'year',
      title: 'Contract year',
      type: 'select',
      value: selectedYear?.toString() || defaultYear?.value?.toString(),
      operator: '$or',
      options: applicableFvsYears
        ?.sort((a, b) => Number.parseInt(b.value, 10) - Number.parseInt(a.value, 10))
        ?.map((year) => ({
          label: year.label,
          value: year.value,
        })),
      expanded: true,
      hideClear: true,
    },
    {
      title: 'Federal standards',
      key: FEDERAL_STANDARDS_HIERARCHY,
      expanded: true,
      filters: [
        {
          key: FED_STANDARD_CODE_FILTER,
          title: 'Federal standard code',
          component: FederalStandardCodeFilterItem,
          permanent: false,
          operator: '$exact',
          value: selectedOptions?.fedStandardCode || '',
        },
        {
          key: VEHICLE_GROUP_FILTER,
          title: 'Vehicle group',
          component: VehicleGroupFilterItem,
          permanent: false,
          operator: '$exact',
          value: selectedOptions?.vehicleGroup || '',
        },
        {
          key: VEHICLE_TYPE_FILTER,
          title: 'Vehicle type',
          component: VehicleTypeFilterItem,
          permanent: false,
          operator: '$exact',
          value: selectedOptions?.vehicleType || '',
        },
      ],
    },
  ], [vehicleClassificationOptions, defaultYear?.value, applicableFvsYears, selectedYear, selectedOptions]);

  const handleClearAll = () => {
    setFvsContextData('SET_SELECTED_OPTIONS', {
      fedStandardCode: '',
      vehicleGroup: '',
      vehicleType: '',
      year: defaultYear?.value?.toString(),
    });
  };

  // 'Reset All' keeps the contract year and clears every other filter. The
  // panel restores the values it was initialized with, so it has to be
  // remounted for selections carried over from another FVS page to go away.
  const { filterPanelKey, handleClearAll: handleResetAll } =
    useFilterPanelReset(handleClearAll);

  const handleFiltersChange = (filters) => {
    const fedStandardCode = filters?.find(
      (filter) => filter.key === FED_STANDARD_CODE_FILTER,
    );
    const year = filters?.find((filter) => filter.key === 'year');
    const vehcType = filters?.find((filter) => filter.key === VEHICLE_TYPE_FILTER);
    const vehcGroup = filters?.find((filter) => filter.key === VEHICLE_GROUP_FILTER);

    let optionsChanged = false;
    const newOptions = { ...selectedOptions };
    let shouldClearDocumentMessage = false;

    // Check if federal standard code changed (including removal)
    if (
      fedStandardCode !== undefined &&
      fedStandardCode?.value !== selectedOptions?.fedStandardCode
    ) {
      newOptions.fedStandardCode = fedStandardCode?.value || '';
      optionsChanged = true;
      shouldClearDocumentMessage = true;

      // When fedStandardCode changes, always clear dependent filters
      newOptions.vehicleGroup = '';
      newOptions.vehicleType = '';
    }

    // Check if vehicle group changed (including removal)
    // If vehcGroup is undefined, it means it's not in the filters array (removed)
    if (vehcGroup !== undefined) {
      if (vehcGroup?.value !== selectedOptions?.vehicleGroup) {
        newOptions.vehicleGroup = vehcGroup?.value || '';
        optionsChanged = true;

        // When vehicleGroup changes, always clear dependent vehicleType filter
        newOptions.vehicleType = '';
      }
    } else if (selectedOptions?.vehicleGroup) {
      // Filter was removed from the array but still in selectedOptions
      newOptions.vehicleGroup = '';
      newOptions.vehicleType = '';
      optionsChanged = true;
    }

    // Check if vehicle type changed (including removal)
    // If vehcType is undefined, it means it's not in the filters array (removed)
    if (vehcType !== undefined) {
      if (vehcType?.value !== selectedOptions?.vehicleType) {
        newOptions.vehicleType = vehcType?.value || '';
        optionsChanged = true;
      }
    } else if (selectedOptions?.vehicleType) {
      // Filter was removed from the array but still in selectedOptions
      newOptions.vehicleType = '';
      optionsChanged = true;
    }

    // Update options if any changed
    if (optionsChanged) {
      if (shouldClearDocumentMessage) {
        removeFvsMessageById('DOCUMENT');
      }
      setFvsContextData('SET_SELECTED_OPTIONS', newOptions);
    }

    // Check if year changed
    if (year !== undefined && year?.value !== selectedYear) {
      setFvsContextData('SET_SELECTED_YEAR', Number.parseInt(year?.value, 10));
    }

    // Check if all filters are cleared (vehicleType, vehicleGroup, and fedStandardCode are all empty)
    const allFiltersCleared =
      (!vehcType?.value || vehcType?.value === '') &&
      (!vehcGroup?.value || vehcGroup?.value === '') &&
      (!fedStandardCode?.value || fedStandardCode?.value === '');

    if (allFiltersCleared && optionsChanged) {
      handleClearAll();
    }
  };

  if (isApplicableFvsYearsLoading || !selectedYear) {
    return null;
  }

  return (
    <ComponentFilterPanel
      key={`fvs-public-filters-${filterPanelKey}`}
      clearButtonLabel="Reset All"
      filterStructure={filterStructure}
      setQueryFiltersState={handleFiltersChange}
      handleClearAll={handleResetAll}
      showClearIcon
      retainFilters
    />
  );
};

FvsFilter.propTypes = {
  onFilter: PropTypes.func.isRequired,
};

export default FvsFilter;
